import { ApolloQueryResult, useQuery } from '@apollo/client'

import {
  Exact,
  InputMaybe,
  LinesheetsQueryCADocument,
  LinesheetsQueryCAQuery,
  PriceTypesQueryCADocument,
  ShippingBillingQueryCADocument,
} from '__generated__/atlas-types'

import { relayConnectionToArray } from 'utils/transformations/graphql'

import { atlasClient } from 'graphql/client'

import {
  LinesheetsQuery,
  PriceTypesCAParams,
  PriceTypesQuery,
  ShippingBillingCAParams,
  ShippingBillingQuery,
} from './SecondStepVerticalHorizontal.types'
import { getRetailerPrimaryLocationOption } from './SecondStepVerticalHorizontal.utils'

export const useLinesheets = (
  brandId: string,
  retailerId?: string,
): LinesheetsQuery & {
  refetchLinesheets: (
    variables?:
      | Partial<
          Exact<{
            brandId: string
            retailerId?: InputMaybe<string> | undefined
          }>
        >
      | undefined,
  ) => Promise<ApolloQueryResult<LinesheetsQueryCAQuery>>
} => {
  const {
    loading,
    error,
    data,
    refetch: refetchLinesheets,
  } = useQuery(LinesheetsQueryCADocument, {
    client: atlasClient,
    variables: {
      brandId,
      retailerId,
    },
    skip: !brandId,
    notifyOnNetworkStatusChange: true,
  })

  return {
    linesheets:
      !loading && !error && data?.collections
        ? relayConnectionToArray(data?.collections)
        : [],
    linesheetsQueryLoading: loading,
    refetchLinesheets,
  }
}

export const formatPriceTypesCA = ({
  data,
  loading,
  error,
}: PriceTypesCAParams): PriceTypesQuery => ({
  priceTypes:
    !loading && !error && data?.priceTypes
      ? relayConnectionToArray(data?.priceTypes)
      : [],
  priceTypeQueryLoading: loading,
})

export const usePriceTypes = (
  brandId: string,
  collectionId?: string,
  retailerId?: string,
): PriceTypesQuery => {
  const { loading, error, data } = useQuery(PriceTypesQueryCADocument, {
    client: atlasClient,
    variables: {
      brandId: brandId,
      collectionId: collectionId || '',
      retailerId: retailerId || '',
    },
    skip: !brandId || !collectionId || !retailerId,
  })

  return formatPriceTypesCA({ loading, error, data })
}

export const formatShippingBillingCA = ({
  data,
  loading,
  error,
}: ShippingBillingCAParams): ShippingBillingQuery => {
  if (!loading && !error && data?.brands) {
    const connectedAccountsEdges = relayConnectionToArray(data.brands)[0]
      ?.connectedAccounts?.edges

    if (!connectedAccountsEdges?.length) {
      return {
        billingAddresses: [],
        paymentMethods: [],
        shippingAddresses: [],
        shippingMethods: [],
        shippingBillingQueryLoading: loading,
      }
    }

    const {
      billingAddresses = [],
      paymentMethods = [],
      shippingAddresses = [],
      shippingMethods = [],
      node: { publicRetailerProfile = null },
    } = connectedAccountsEdges[0]

    const retailerLocationOption = getRetailerPrimaryLocationOption(
      publicRetailerProfile?.primaryLocation,
    )

    return {
      billingAddresses,
      paymentMethods,
      shippingAddresses,
      shippingMethods,
      retailerLocationOption,
      shippingBillingQueryLoading: loading,
    }
  }

  return {
    billingAddresses: [],
    paymentMethods: [],
    shippingAddresses: [],
    shippingMethods: [],
    shippingBillingQueryLoading: loading,
  }
}

export const useShippingBilling = (
  isLiteRetailer: boolean,
  brandId: string,
  retailerId?: string,
): ShippingBillingQuery => {
  const { loading, error, data } = useQuery(ShippingBillingQueryCADocument, {
    client: atlasClient,
    variables: {
      brandId,
      retailerId: retailerId || '',
    },
    skip: isLiteRetailer || !brandId || !retailerId,
  })

  return formatShippingBillingCA({ loading, error, data })
}
